import type { Hotel } from '@elara/client-core/models/HotelModel';

import type { BaseDetailCardLayoutProps, CardLayout } from './DetailCardLayout.js';

import { Logo } from '#ui/logo.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { CardDescription, DetailCardLayout } from './DetailCardLayout.js';

export interface HotelCardProps extends BaseDetailCardLayoutProps {
  hotel: Hotel;
  /** Labelled actions rendered as a row below the content (`lg` only) — see `DetailCardLayout`. */
  footerActions?: React.ReactNode;
  /** `'row'` lays the card out photo-left for a hover popover — see `DetailCardLayout`. */
  layout?: CardLayout;
}

export function HotelCard({
  hotel,
  size = 'default',
  className,
  actions,
  footerActions,
  layout,
  href,
  onClick,
}: HotelCardProps) {
  const displayTitle = hotel?.name;
  const description = hotel?.long_description;
  const address = hotel?.shortAddress;

  return (
    <DetailCardLayout
      images={hotel?.previewImages}
      title={displayTitle}
      type="hotel"
      size={size}
      actions={actions}
      footerActions={footerActions}
      layout={layout}
      className={className}
      href={href}
      onClick={onClick}
    >
      {/* One wrapping row, matching the other cards: these are short facts and a stack of them turned
          a card into a column of one-word lines. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {/* WHERE it is — the one fact this card was missing everywhere, not just in the popover. A
            place card leads with category, price band and address; a hotel showing none of them was
            the odd one out, and once the popover stopped carrying prose it had a name and nothing
            else. `min-w-0` + `truncate` so a long street line ellipses rather than widening the row. */}
        {address && (
          <div className="flex min-w-0 items-center gap-1">
            <MaterialIcon icon="location_on" size={IconSizes.xs} />
            <span className="truncate">{address}</span>
          </div>
        )}
        {hotel.isSerandipiansAffiliated && (
          <div className="flex min-w-0 items-center gap-1">
            {/* A Material glyph sits inside its box with ~2/24 clear on each side; the symbol runs to its
                edges, so it is drawn at that inner size in a box of the icons' size to look the same size. */}
            <span className="flex size-4 shrink-0 items-center justify-center">
              <Logo variant="symbol" className="size-[13px] text-current" />
            </span>
            <span className="truncate">Elara Benefits available</span>
          </div>
        )}
      </div>

      <CardDescription size={size}>{description}</CardDescription>
    </DetailCardLayout>
  );
}
